import { BiRadioCircleMarked } from 'react-icons/bi';

import type { DrillHole } from '~api/services';
import { PropertyGroup, PropertyItem } from '~components';
import { ScrollArea } from '~components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '~components/ui/sheet';
import { formatDate } from '~utils/date';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type DrillHoleDetailDrawerProps = {
  /** The drill hole data to display */
  hole: DrillHole | null;
  /** The container element to render the drawer into */
  container?: HTMLElement;
  /** Whether the drawer is open */
  open: boolean;
  /** Callback when the drawer is closed */
  onClose: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const GRID_PARAMETERS: { label: string; getValue: (hole: DrillHole) => string | number; unit?: string }[] = [
  { label: 'depth', getValue: (h) => h.depth.toFixed(2), unit: 'm' },
  { label: 'bearing', getValue: (h) => h.bearing.toFixed(0), unit: '°' },
  { label: 'dip', getValue: (h) => h.dip.toFixed(0), unit: '°' },
  { label: 'diameter', getValue: (h) => String(h.diameter) },
  { label: 'redrill', getValue: (h) => (h.redrill ? 'Yes' : 'No') },
];

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sheet component to display details of a neighbouring drill hole, including
 * parameters such as depth, bearing, dip, diameter, and source pattern.
 *
 * @example
 * ```tsx
 *   <DrillHoleDetailDrawer
 *     hole={selectedDrillHole}
 *     container={drawerContainer}
 *     open={drillHoleDrawerOpen}
 *     onClose={() => setDrillHoleDrawerOpen(false)}
 *   />
 * ```
 */
const DrillHoleDetailDrawer = ({ hole, container, open, onClose }: DrillHoleDetailDrawerProps) => {
  const sourceLastUpdated = hole ? formatDate(new Date(hole.datetime_updated)) : null;
  const drillType = hole ? `${hole.drill_model ?? '-'}${hole.drill_serial ? ` (${hole.drill_serial})` : ''}` : '-';

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={false}>
      <SheetContent
        portalContainer={container ?? document.body}
        side="inline-end"
        strategy="absolute"
        showOverlay={false}
        className="grow border-t-0"
        data-open={open}
        aria-describedby={undefined}
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader>
          {hole ? (
            <SheetTitle className="flex items-center gap-2">
              <BiRadioCircleMarked className="rounded-full bg-primary/10 p-1 text-primary" size={32} />
              Hole: <span>{hole.hole_name}</span>
            </SheetTitle>
          ) : (
            <SheetTitle>Hole Details</SheetTitle>
          )}
        </SheetHeader>
        <div className="flex min-h-0 flex-1">
          <ScrollArea className="h-full w-full" type="auto">
            {hole && (
              <div className="space-y-6 p-6">
                <PropertyItem label="pattern" value={hole.pattern_name} variant="featured" />
                <div className="border-b border-b-border" />
                <PropertyGroup withBorder>
                  {GRID_PARAMETERS.map(({ label, getValue, unit }) => (
                    <PropertyItem key={label} label={label} value={getValue(hole)} unit={unit} contained />
                  ))}
                </PropertyGroup>
                <PropertyItem label="drill type" value={drillType} />
                <PropertyItem label="source" value={hole.source} />
                <PropertyItem label="source last updated" value={sourceLastUpdated ?? '-'} />
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { DrillHoleDetailDrawer };
